// Sidebar red-dot counts. Supplier-side new-order/delivery counts plus the
// buyer's "received orders" (REVIEWED) purchase count for /buyer-sales.
// Gated on token + portal so nothing fires on unauthenticated boot.
import useSWR from "swr"
import { DeliveryManagementApi, OrderApi, PullSheetApi } from "../../../apis"

interface CountResponse {
  count?: number
}

export interface SidebarBadges {
  byLink: Record<string, number | undefined>
}

export function useSidebarBadges(opts: {
  accessToken: string | null | undefined
  isSupplier: boolean
  isBuyer: boolean
}): SidebarBadges {
  const enabled = !!opts.accessToken && opts.isSupplier
  const buyerEnabled = !!opts.accessToken && opts.isBuyer

  const { data: customerOrderCount } = useSWR<CountResponse>(
    enabled ? "swr.order.count.customer" : null,
    () => OrderApi.count({ type: "PURCHASE", orderStatus: "REVIEWED" }),
    { refreshInterval: 60000, revalidateOnFocus: true },
  )
  const { data: salesOrderCount } = useSWR<CountResponse>(
    enabled ? "swr.order.count.sales" : null,
    () => OrderApi.count({ type: "SALES", orderStatus: "REVIEWED" }),
    { refreshInterval: 60000, revalidateOnFocus: true },
  )
  const { data: deliveryStats } = useSWR(
    enabled ? "swr.delivery.stats.sidebar" : null,
    () => DeliveryManagementApi.stats(),
    { refreshInterval: 60000, revalidateOnFocus: true },
  )
  const { data: shippingShortageCount } = useSWR<CountResponse>(
    enabled ? "swr.shipping.shortage.count" : null,
    () => PullSheetApi.shortageCount(),
    { refreshInterval: 60000, revalidateOnFocus: true },
  )
  const { data: buyerPurchaseCount } = useSWR<CountResponse>(
    buyerEnabled ? "swr.order.count.buyer-purchase" : null,
    () =>
      OrderApi.list({
        filter: {
          type: "",
          orderStatus: "REVIEWED",
          deliveryNoteStatus: "",
          businessId: "",
          startDate: "",
          endDate: "",
          invoiceStatus: "",
          paymentStatus: "",
          paymentTermCondition: "",
          excel: "false",
        },
        offset: { limit: 1, page: 1 },
      }),
    { refreshInterval: 60000, revalidateOnFocus: true },
  )

  // Sidebar badge sums the delivery-management sub-tabs that still need action —
  // Хүлээлтэнд + Маршрут + Хүлээлцэх + Идэвхтэй + Амжилтгүй. "Хүргэсэн" (delivered
  // archive) and "Бэлэн төлөлт" (COD) are excluded — the badge reflects the
  // outstanding delivery workload, not finished deliveries or cash settlement.
  const dt = deliveryStats?.tabs
  const deliveryTotal = dt
    ? (dt.pending ?? 0) + (dt.routes ?? 0) + (dt.handover ?? 0) + (dt.active ?? 0) +
      (dt.failed ?? 0)
    : undefined

  return {
    byLink: {
      "/supplier-sales": salesOrderCount?.count,
      "/supplier-purchase": customerOrderCount?.count,
      "/supplier-delivery": deliveryTotal,
      "/supplier-shipping": shippingShortageCount?.count,
      "/buyer-sales": buyerPurchaseCount?.count,
    },
  }
}
